import { sendMessageInitialValues, sendMessageSchema } from 'constants/schemaValidations';
import { useFormik } from 'formik';
import useTranslate from 'hooks/useTranslate';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useBucketUrlMutation, useUploadFileMutation } from 'services';
import useChatStore, { setNewMessageAlert } from 'store/chat';
import { setIsLoading } from 'store/common';
import { endTyping, sendMessage, startTyping } from 'store/socket';
import { convertJpegToPng, getFileExtension, getFileType } from 'utils';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { ALLOWEDVIDEOFORMATS } from 'constants';
import useLocalizationStore from 'store/localization';

const MessageForm: React.FC<any> = () => {
	const { receiverId, roomId = '' } = useParams<string>();
	const { translate } = useTranslate();
	const { isTypingRooms }: Record<string, any> = useChatStore((state) => state || []);
	const { activeLanguage }: any = useLocalizationStore((state) => state);
	const [fileType, setFileType] = useState('');

	const { mutateAsync: bucketUrlMutation } = useBucketUrlMutation();
	const { mutateAsync: uploadFileMutation } = useUploadFileMutation();

	const emojiRef = useRef<any>(null);
	const emojiBtnRef = useRef<any>(null);

	const [openEmojiPicker, setOpenEmojiPicker] = useState<boolean>(false);

	useEffect(() => {
		if (fileType) {
			document.getElementById('fileInput')?.click();
		}

		return () => setFileType('');
	}, [fileType]);

	const formik = useFormik({
		initialValues: { ...sendMessageInitialValues, receiverId },
		validationSchema: sendMessageSchema(translate),
		enableReinitialize: true,
		onSubmit: async (values) => {
			sendMessage(values);
			handleStopTyping();
			formik.resetForm();
		},
	});

	const handleTyping = () => {
		startTyping(roomId);
	};

	const handleStopTyping = () => {
		endTyping(roomId);
	};

	useEffect(() => {
		const typingTimeout = setTimeout(() => {
			handleStopTyping();
		}, 3000);

		return () => clearTimeout(typingTimeout);
	}, [isTypingRooms]);

	const onAssetsChange = async (e: any) => {
		const files: any = Array.from(e.target.files);
		if (files?.length > 0) {
			if (files.length > 10) {
				toast.error(translate('Validation.uploadMaxTenMedia'));
				return;
			}

			const maxFileSize = 10 * 1024 * 1024;
			let fileSizeExceed = false;
			let videoCount: number = 0;
			let invalidVideoFormat = false;
			const allowedVideoFormatsString = ALLOWEDVIDEOFORMATS.map(
				(format: any) => format.split('/')[1],
			).join(', ');

			try {
				for (const file of files) {
					// Check file size and type
					if (file.size > maxFileSize) {
						fileSizeExceed = true;
					}
					if (getFileType(file?.type) === 'video') {
						videoCount += 1;
						if (!ALLOWEDVIDEOFORMATS.includes(file.type)) {
							invalidVideoFormat = true;
						}
					}
				}

				if (videoCount > 1) {
					toast.error(translate('Validation.youCanSendASingleVideoAtOnce'));
					return;
				}

				if (fileSizeExceed) {
					toast.error(translate('Validation.fileSizeShouldBeLessThan', { size: '10 MB' }));
					return;
				}
			} catch (error) {
				console.error('An error occurred while validating files:', error);
				toast.error(translate('ToastMessage.anUnexpectedErrorOccurred'));
			}

			// Proceed with the upload logic

			if (videoCount > 1) {
				toast.error(translate('ToastMessage.sendSingleVideoAtOnce'));
				return;
			}

			if (invalidVideoFormat) {
				toast.error(
					translate('ToastMessage.invalidVideoFormat', { formats: allowedVideoFormatsString }),
				);
				return;
			}

			if (fileSizeExceed) {
				toast.error(translate('Validation.fileSizeShouldBeLessThan', { size: '10 MB' }));
				return;
			} else {
				const convertedFilesPromises = files.map(async (file: any) => {
					if (file.type.startsWith('image/') && file.type !== 'image/png') {
						const pngBlob = await convertJpegToPng(file); // Convert to PNG
						const pngFile = new File([pngBlob], file.name.replace(/\.[^/.]+$/, '.png'), {
							type: 'image/png',
						});
						return pngFile;
					}
					return file; // Keep other files unchanged (videos, other images)
				});

				const convertedFiles = await Promise.all(convertedFilesPromises);

				// Proceed with the upload logic after conversion
				await uploadFiles(convertedFiles);
			}
		}
	};

	const uploadFiles = async (files: any) => {
		setIsLoading(true);
		const bucketArr = await getFileArr(files);
		const response = await bucketUrlMutation(bucketArr);
		if (response?.success) {
			const uploadData = response?.data?.map((resData: any, index: number) => {
				const { type } = files[index];
				return { ...resData, mediaType: getFileType(type) };
			});
			await uploadMedia(uploadData, files);
			const formData = response?.data?.map((data: any, index: number) => {
				return {
					message: data?.location,
					receiverId: receiverId,
					attachmentType: getFileType(files[index].type),
				};
			});
			if (formData?.length > 0) {
				formData?.map((message: any) => sendMessage(message));
			}
		}
		setIsLoading(false);
	};

	const uploadMedia = async (values: any, files: any) => {
		try {
			const mediaItems = values || [];

			const uploadPromises = mediaItems.map(async (media: any, index: number) => {
				const requestOptions = {
					method: 'PUT',
					headers: {
						'Content-Type': media.mediaType,
					},
					body: files[index],
				};

				return uploadFileMutation({
					url: media.url,
					requestOptions,
				});
			});

			const responses = await Promise.all(uploadPromises);
			if (responses.every((response) => response.success)) {
				console.log('All files uploaded successfully:', responses);
			} else {
				console.error('Some files failed to upload:', responses);
			}
		} catch (error) {
			console.error('An error occurred during file uploads:', error);
		}
	};

	const getFileArr = async (mediaData: any) => {
		return {
			type: 'CHAT',
			extension: mediaData.map((file: any) => getFileExtension(file?.name)),
			contentType: mediaData.map((file: any) => file.type),
			originalFileName: mediaData.map((file: any) => file.name),
		};
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [openEmojiPicker]);

	const handleAddEmoji = (emojiData: any) => {
		const emoji = emojiData.native;
		const messageInput = document.querySelector('textarea[name="message"]') as HTMLTextAreaElement;

		if (messageInput) {
			const { selectionStart, selectionEnd } = messageInput;
			const message = formik.values.message || '';

			// Insert emoji at the cursor position
			const newMessage = message.slice(0, selectionStart) + emoji + message.slice(selectionEnd);

			// Update the message with the new text and reposition the cursor
			formik.setFieldValue('message', newMessage);

			// Set the cursor position after the inserted emoji
			setTimeout(() => {
				messageInput.selectionStart = messageInput.selectionEnd = selectionStart + emoji.length;
			}, 0);
		}
	};

	const handleClickOutside = (e: any) => {
		if (!emojiRef?.current?.contains(e.target) && !emojiBtnRef?.current?.contains(e.target)) {
			setOpenEmojiPicker(e.unified);
		}
	};

	return (
		<Form className="form" onSubmit={formik.handleSubmit}>
			<div className="send-msg-field">
				<div className="d-flex align-items-center gap-2 gap-md-3">
					<Form.Group className="position-relative flex-1">
						<Form.Control
							as="textarea"
							placeholder={translate('Chat.typeMsg')}
							name="message"
							className="rounded-pill"
							onChange={(value: any) => {
								setNewMessageAlert(false);
								handleTyping();
								formik.handleChange(value);
							}}
							onBlur={formik.handleBlur}
							value={formik.values?.message}
							autoComplete="off"
						/>

						<div className="edit-action attach-btn position-absolute">
							<Dropdown drop="up">
								<Dropdown.Toggle
									variant="success"
									id="dropdown-basic"
									className="p-0 border-0 rounded-circle bg-transparent lh-1"
								>
									<i className="ri-attachment-line"></i>
								</Dropdown.Toggle>

								<Dropdown.Menu>
									<input
										type="file"
										multiple
										accept={fileType}
										id="fileInput"
										name="fileInput"
										style={{ display: 'none' }}
										onChange={onAssetsChange}
									/>
									<Dropdown.Item
										onClick={() => {
											setNewMessageAlert(false);
											setFileType('image/*,video/*');
										}}
									>
										<i className="ri-image-line"></i> {translate('Chat.media')}
									</Dropdown.Item>
									<Dropdown.Item
										onClick={() => {
											setNewMessageAlert(false);
											setFileType('.pdf,.doc,.docx,.xls,.xlsx,.txt');
										}}
									>
										<i className="ri-file-line"></i> {translate('Chat.documents')}
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						</div>
					</Form.Group>
					<div className="emoji-btn position-relative">
						<Button
							className="border-0 p-0 bg-transparent"
							onClick={() => {
								setNewMessageAlert(false);
								setOpenEmojiPicker(!openEmojiPicker);
							}}
							ref={emojiBtnRef}
						>
							<i className="ri-emotion-happy-fill"></i>
						</Button>
						{openEmojiPicker && (
							<div className="emoji-picker-box" ref={emojiRef} dir="rtl">
								<Picker
									data={data}
									onEmojiSelect={handleAddEmoji}
									showPreview={false}
									className="emoji-picker"
									locale={activeLanguage}
									emojiButtonSize={30}
									emojiSize={18}
									previewPosition={'none'}
								/>
							</div>
						)}
					</div>
					<Button className="filled-btn border-0 p-0 rounded-circle" type="submit">
						<i className="ri-send-plane-fill"></i>
					</Button>
				</div>
			</div>
		</Form>
	);
};

export default MessageForm;
